import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

// Security-hardening task, item 3: admin self-service TOTP 2FA management —
// GET/POST /auth/2fa/* (routes/authRoutes.js). Self-service only, an admin
// manages their OWN account's 2FA (see totpService.js's own header
// comment) — there is no id in this query key because "am I enabled" is a
// property of the logged-in session, not a list.
const TWO_FACTOR_KEY = ['auth', 'twoFactorStatus'];

export function useTwoFactorStatus() {
  return useQuery({
    queryKey: TWO_FACTOR_KEY,
    queryFn: () => apiClient.get('/auth/2fa/status'),
    select: (data) => data.enabled,
  });
}

// POST /auth/2fa/setup — generates a fresh secret + QR, held server-side in
// the admin's own session until confirmed (nothing persisted to the DB
// yet). Returns { secret, otpauthUrl, qrDataUrl }.
export function useStartTwoFactorSetup() {
  return useMutation({
    mutationFn: () => apiClient.post('/auth/2fa/setup'),
  });
}

// POST /auth/2fa/confirm — proves the admin scanned the QR, persists the
// secret, and returns the one-time-visible recovery codes.
export function useConfirmTwoFactorSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token) => apiClient.post('/auth/2fa/confirm', { token }),
    onSuccess: () => {
      queryClient.setQueryData(TWO_FACTOR_KEY, { enabled: true });
    },
  });
}

// POST /auth/2fa/disable — requires the admin's current password.
export function useDisableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (password) => apiClient.post('/auth/2fa/disable', { password }),
    onSuccess: () => {
      queryClient.setQueryData(TWO_FACTOR_KEY, { enabled: false });
    },
  });
}
